import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import DirectionsCarRoundedIcon from '@mui/icons-material/DirectionsCarRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import LocalGasStationRoundedIcon from '@mui/icons-material/LocalGasStationRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import SubscriptionsRoundedIcon from '@mui/icons-material/SubscriptionsRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import ProaDatePicker from '../../../components/Common/ProaDatePicker';

import {
  createProjectVitalFinanceMovement,
  getProjectVitalFinanceOverview,
  updateProjectVitalFinanceMovement,
} from '../services/projectVitalService';
import type {
  ProjectVitalFinanceAccount,
  ProjectVitalFinanceMovement,
  ProjectVitalFinanceMovementType,
  ProjectVitalFinancialScope,
} from '../types/projectVital';

type Props = {
  open: boolean;
  onClose: () => void;
  financialScope?: ProjectVitalFinancialScope;
  movement?: ProjectVitalFinanceMovement | null;
  initialMovementType?: ProjectVitalFinanceMovementType;
};

type CategoryOption = {
  value: string;
  label: string;
  icon: React.ReactNode;
};

type SaveMode = 'close' | 'continue';

const PERSONAL_EXPENSE_CATEGORIES: CategoryOption[] = [
  { value: 'Supermercado', label: 'Supermercado', icon: <ShoppingCartRoundedIcon /> },
  { value: 'Vivienda', label: 'Vivienda', icon: <HomeRoundedIcon /> },
  { value: 'Restaurantes', label: 'Restaurantes', icon: <RestaurantRoundedIcon /> },
  { value: 'Gasolina', label: 'Gasolina', icon: <LocalGasStationRoundedIcon /> },
  { value: 'Facturas', label: 'Facturas', icon: <BoltRoundedIcon /> },
  { value: 'Compras', label: 'Compras', icon: <ShoppingBagRoundedIcon /> },
  { value: 'Transporte', label: 'Transporte', icon: <DirectionsCarRoundedIcon /> },
  { value: 'Suscripciones', label: 'Suscripciones', icon: <SubscriptionsRoundedIcon /> },
  { value: 'Salud', label: 'Salud', icon: <PaymentsRoundedIcon /> },
  { value: 'Formación', label: 'Formación', icon: <SchoolRoundedIcon /> },
  { value: 'Viajes', label: 'Viajes', icon: <DirectionsCarRoundedIcon /> },
  { value: 'Regalos', label: 'Regalos', icon: <CardGiftcardRoundedIcon /> },
  { value: 'Otros', label: 'Otros', icon: <MoreHorizRoundedIcon /> },
];

const PROFESSIONAL_EXPENSE_CATEGORIES: CategoryOption[] = [
  { value: 'Material profesional', label: 'Material', icon: <ShoppingBagRoundedIcon /> },
  { value: 'Gestoría', label: 'Gestoría', icon: <WorkRoundedIcon /> },
  { value: 'Seguros profesionales', label: 'Seguros', icon: <ReceiptLongRoundedIcon /> },
  { value: 'Cotizaciones sociales', label: 'Cotizaciones', icon: <PaymentsRoundedIcon /> },
  { value: 'Restaurantes', label: 'Restaurantes', icon: <RestaurantRoundedIcon /> },
  { value: 'Transporte', label: 'Transporte', icon: <DirectionsCarRoundedIcon /> },
  { value: 'Gasolina', label: 'Gasolina', icon: <LocalGasStationRoundedIcon /> },
  { value: 'Suscripciones', label: 'Suscripciones', icon: <SubscriptionsRoundedIcon /> },
  { value: 'Formación', label: 'Formación', icon: <SchoolRoundedIcon /> },
  { value: 'Impuestos y tasas', label: 'Impuestos', icon: <ReceiptLongRoundedIcon /> },
  { value: 'Otros', label: 'Otros', icon: <MoreHorizRoundedIcon /> },
];

const PERSONAL_INCOME_CATEGORIES: CategoryOption[] = [
  { value: 'Nómina', label: 'Nómina', icon: <WorkRoundedIcon /> },
  { value: 'Prestación', label: 'Prestación', icon: <PaymentsRoundedIcon /> },
  { value: 'Reembolso', label: 'Reembolso', icon: <ReplayRoundedIcon /> },
  { value: 'Venta', label: 'Venta', icon: <SellRoundedIcon /> },
  { value: 'Intereses / rendimientos', label: 'Intereses', icon: <TrendingUpRoundedIcon /> },
  { value: 'Ayuda / regalo', label: 'Ayuda / regalo', icon: <CardGiftcardRoundedIcon /> },
  { value: 'Otros', label: 'Otros', icon: <MoreHorizRoundedIcon /> },
];

const PROFESSIONAL_INCOME_CATEGORIES: CategoryOption[] = [
  { value: 'Clínica / actividad', label: 'Clínica / actividad', icon: <StorefrontRoundedIcon /> },
  { value: 'Otros clientes', label: 'Otros clientes', icon: <WorkRoundedIcon /> },
  { value: 'Reembolso', label: 'Reembolso', icon: <ReplayRoundedIcon /> },
  { value: 'Intereses / rendimientos', label: 'Intereses', icon: <TrendingUpRoundedIcon /> },
  { value: 'Otros', label: 'Otros', icon: <MoreHorizRoundedIcon /> },
];

export const PROJECT_VITAL_FINANCE_CATEGORIES = Array.from(
  new Set([
    ...PERSONAL_EXPENSE_CATEGORIES,
    ...PROFESSIONAL_EXPENSE_CATEGORIES,
    ...PERSONAL_INCOME_CATEGORIES,
    ...PROFESSIONAL_INCOME_CATEGORIES,
  ].map((item) => item.value)),
);

function localIsoDate(offsetDays = 0) {
  const value = new Date();
  value.setDate(value.getDate() + offsetDays);
  value.setMinutes(value.getMinutes() - value.getTimezoneOffset());
  return value.toISOString().slice(0, 10);
}

function todayIso() { return localIsoDate(); }
function yesterdayIso() { return localIsoDate(-1); }
function currentMonth() { return todayIso().slice(0, 7); }

function formatCurrency(value: number) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function normalizeAmountInput(value: string) {
  const normalized = value.replace(/\./g, ',').replace(/[^0-9,]/g, '');
  const [integerPart, ...decimalParts] = normalized.split(',');
  return decimalParts.length === 0
    ? integerPart
    : `${integerPart},${decimalParts.join('').slice(0, 2)}`;
}

function getDateMode(value: string): 'today' | 'yesterday' | 'custom' {
  if (value === todayIso()) return 'today';
  if (value === yesterdayIso()) return 'yesterday';
  return 'custom';
}

function categoriesFor(scope: ProjectVitalFinancialScope, type: ProjectVitalFinanceMovementType) {
  if (type === 'ingreso') {
    return scope === 'professional' ? PROFESSIONAL_INCOME_CATEGORIES : PERSONAL_INCOME_CATEGORIES;
  }
  return scope === 'professional' ? PROFESSIONAL_EXPENSE_CATEGORIES : PERSONAL_EXPENSE_CATEGORIES;
}

function firstCategory(scope: ProjectVitalFinancialScope, type: ProjectVitalFinanceMovementType) {
  return categoriesFor(scope, type)[0]?.value ?? 'Otros';
}

function accountScopeLabel(scope: ProjectVitalFinancialScope) {
  return scope === 'professional' ? 'Profesional' : 'Personal';
}

export default function ProjectVitalFinanceMovementDialog({
  open,
  onClose,
  financialScope = 'personal',
  movement = null,
  initialMovementType = 'gasto',
}: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const amountInputRef = useRef<HTMLInputElement | null>(null);

  const [movementType, setMovementType] = useState<ProjectVitalFinanceMovementType>('gasto');
  const [financialNature, setFinancialNature] = useState<ProjectVitalFinancialScope>(financialScope);
  const [amount, setAmount] = useState('');
  const [movementDate, setMovementDate] = useState(todayIso());
  const [category, setCategory] = useState(firstCategory(financialScope, 'gasto'));
  const [customCategory, setCustomCategory] = useState('');
  const [description, setDescription] = useState('');
  const [accountId, setAccountId] = useState('');
  const [sourceAccountId, setSourceAccountId] = useState('');
  const [destinationAccountId, setDestinationAccountId] = useState('');
  const [showMoreCategories, setShowMoreCategories] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const overviewQuery = useQuery({
    queryKey: ['project-vital', 'finance', currentMonth(), 'global'],
    queryFn: () => getProjectVitalFinanceOverview(currentMonth(), 'global'),
    enabled: open,
  });

  const accounts = useMemo(
    () => (overviewQuery.data?.accounts ?? []).filter((account) => account.is_active),
    [overviewQuery.data],
  );

  const orderedAccounts = useMemo(() => [...accounts].sort((a, b) => {
    const pa = a.financial_scope === financialNature ? 0 : 1;
    const pb = b.financial_scope === financialNature ? 0 : 1;
    return pa !== pb ? pa - pb : a.name.localeCompare(b.name, 'es');
  }), [accounts, financialNature]);

  const categoryOptions = useMemo(
    () => categoriesFor(financialNature, movementType),
    [financialNature, movementType],
  );
  const quickCategoryOptions = useMemo(
    () => categoryOptions.filter((item) => item.value !== 'Otros').slice(0, 6),
    [categoryOptions],
  );
  const moreCategoryOptions = useMemo(() => {
    const quickValues = new Set(quickCategoryOptions.map((item) => item.value));
    return categoryOptions.filter((item) => !quickValues.has(item.value));
  }, [categoryOptions, quickCategoryOptions]);

  useEffect(() => {
    if (!open) return;

    if (movement) {
      const nature = movement.financial_scope ?? financialScope;
      setMovementType(movement.movement_type);
      setFinancialNature(nature);
      setAmount(String(movement.amount));
      setMovementDate(movement.movement_date);
      setDescription(movement.description || '');
      setAccountId(movement.account_id || '');
      setSourceAccountId(movement.source_account_id || '');
      setDestinationAccountId(movement.destination_account_id || '');
      setShowDetails(Boolean(movement.description));

      if (movement.movement_type !== 'transferencia') {
        const known = categoriesFor(nature, movement.movement_type).map((item) => item.value);
        const categoryFromRecord = movement.category === 'Cuotas sociales'
          ? 'Cotizaciones sociales'
          : movement.category;
        if (categoryFromRecord && known.includes(categoryFromRecord)) {
          setCategory(categoryFromRecord);
          setCustomCategory('');
          setShowMoreCategories(!categoriesFor(nature, movement.movement_type)
            .filter((item) => item.value !== 'Otros').slice(0, 6)
            .some((item) => item.value === categoryFromRecord));
        } else if (categoryFromRecord) {
          setCategory('Otros');
          setCustomCategory(categoryFromRecord);
          setShowMoreCategories(true);
        }
      }
      return;
    }

    setMovementType(initialMovementType);
    setFinancialNature(financialScope);
    setAmount('');
    setMovementDate(todayIso());
    setCategory(firstCategory(financialScope, initialMovementType));
    setCustomCategory('');
    setDescription('');
    setAccountId('');
    setSourceAccountId('');
    setDestinationAccountId('');
    setShowMoreCategories(false);
    setShowDetails(false);
  }, [open, movement, financialScope, initialMovementType]);

  useEffect(() => {
    if (!open || movement || movementType === 'transferencia' || accountId || accounts.length === 0) return;
    const preferred = accounts.find((account) => account.financial_scope === financialNature) ?? accounts[0];
    setAccountId(preferred.id);
  }, [open, movement, movementType, accountId, accounts, financialNature]);

  useEffect(() => {
    if (!open || movement || movementType === 'transferencia') return;
    setCategory(firstCategory(financialNature, movementType));
    setCustomCategory('');
    setShowMoreCategories(false);
  }, [open, movement, movementType, financialNature]);

  const effectiveCategory = category === 'Otros' ? customCategory.trim() : category;
  const selectedAccount = accounts.find((account) => account.id === accountId);
  const selectedSourceAccount = accounts.find((account) => account.id === sourceAccountId);
  const selectedDestinationAccount = accounts.find((account) => account.id === destinationAccountId);
  const accountNatureMismatch = movementType !== 'transferencia'
    && selectedAccount !== undefined && selectedAccount.financial_scope !== financialNature;
  const fiscalPaymentCategory = financialNature === 'professional' && movementType === 'gasto'
    && ['Cotizaciones sociales', 'Cuotas sociales', 'Impuestos y tasas'].includes(effectiveCategory);
  const hasAccounts = accounts.length > 0;
  const numericAmount = Number(amount.replace(',', '.'));
  const dateMode = getDateMode(movementDate);
  const isValid = hasAccounts && !Number.isNaN(numericAmount) && numericAmount > 0 && Boolean(movementDate) && (
    movementType === 'transferencia'
      ? Boolean(sourceAccountId) && Boolean(destinationAccountId) && sourceAccountId !== destinationAccountId
      : Boolean(accountId) && Boolean(effectiveCategory)
  );

  function resetForNextEntry() {
    setAmount('');
    setDescription('');
    setCustomCategory('');
    setShowDetails(false);
    setShowMoreCategories(false);
    if (movementType !== 'transferencia') setCategory(firstCategory(financialNature, movementType));
    window.setTimeout(() => amountInputRef.current?.focus(), 0);
  }

  const mutation = useMutation({
    mutationFn: async (saveMode: SaveMode) => {
      const normalizedAmount = Number(amount.replace(',', '.'));
      const payload = movementType === 'transferencia'
        ? {
            movement_type: 'transferencia' as const,
            financial_scope: null,
            amount: normalizedAmount,
            movement_date: movementDate,
            category: null,
            description: description.trim() || null,
            account_id: null,
            source_account_id: sourceAccountId,
            destination_account_id: destinationAccountId,
          }
        : {
            movement_type: movementType,
            financial_scope: financialNature,
            amount: normalizedAmount,
            movement_date: movementDate,
            category: effectiveCategory,
            description: description.trim() || null,
            account_id: accountId,
            source_account_id: null,
            destination_account_id: null,
          };

      const result = movement
        ? await updateProjectVitalFinanceMovement(movement.id, payload)
        : await createProjectVitalFinanceMovement(payload);
      return { result, saveMode };
    },
    onSuccess: async ({ saveMode }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'finance'] }),
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'finance-history'] }),
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'finance-forecast'] }),
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'professional-monthly-control'] }),
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'professional-cashflow'] }),
      ]);

      enqueueSnackbar(
        movement ? 'Movimiento actualizado.' : movementType === 'transferencia'
          ? 'Transferencia registrada.' : movementType === 'gasto'
            ? 'Gasto registrado.' : 'Ingreso registrado.',
        { variant: 'success' },
      );

      if (!movement && saveMode === 'continue') {
        resetForNextEntry();
        return;
      }
      onClose();
    },
    onError: () => enqueueSnackbar(
      movement ? 'No se pudo actualizar el movimiento.' : 'No se pudo registrar el movimiento.',
      { variant: 'error' },
    ),
  });

  function selectAccount(account: ProjectVitalFinanceAccount) {
    // La cuenta usada y la naturaleza económica son decisiones independientes.
    // Un gasto profesional puede pagarse desde una cuenta personal, y viceversa.
    setAccountId(account.id);
  }

  const compactAccount = (account: ProjectVitalFinanceAccount, selected: boolean, onClick: () => void, disabled = false) => (
    <ButtonBase key={account.id} disabled={disabled} onClick={onClick} sx={{ width: '100%', borderRadius: 2.2, opacity: disabled ? 0.35 : 1 }}>
      <Box sx={{ width: '100%', px: 1.1, py: .85, borderRadius: 2.2, border: `1px solid ${selected ? alpha('#4DD0E1', .55) : alpha('#94a3b8', .12)}`, bgcolor: selected ? alpha('#4DD0E1', .09) : alpha('#ffffff', .015) }}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
          <Box sx={{ minWidth: 0, textAlign: 'left' }}>
            <Typography noWrap sx={{ color: '#e2e8f0', fontWeight: 800, fontSize: '.8rem' }}>{account.name}</Typography>
            <Typography sx={{ color: '#64748b', fontSize: '.66rem' }}>{accountScopeLabel(account.financial_scope)}</Typography>
          </Box>
          <Typography sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '.75rem', whiteSpace: 'nowrap' }}>{formatCurrency(account.current_balance)}</Typography>
        </Stack>
      </Box>
    </ButtonBase>
  );

  return (
    <Dialog open={open} onClose={mutation.isPending ? undefined : onClose} fullWidth fullScreen={fullScreen} maxWidth="sm" PaperProps={{ sx: { bgcolor: '#111c2f', border: fullScreen ? 'none' : '1px solid rgba(148,163,184,.16)', borderRadius: fullScreen ? 0 : 3.5 } }}>
      <DialogTitle sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, fontWeight: 850, borderBottom: '1px solid rgba(148,163,184,.10)' }}>
        {movement ? 'Editar movimiento' : 'Añadir movimiento'}
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <Stack spacing={1.55} sx={{ pt: 1.4, pb: 1 }}>
          {!hasAccounts && !overviewQuery.isLoading ? <Alert severity="info">Primero crea una cuenta desde Finanzas.</Alert> : null}

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: .6 }}>
            {([['gasto', 'Gasto'], ['ingreso', 'Ingreso'], ['transferencia', 'Transferencia']] as const).map(([value, label]) => {
              const selected = movementType === value;
              return <ButtonBase key={value} onClick={() => setMovementType(value)} sx={{ py: .95, px: .5, borderRadius: 2.1, border: `1px solid ${selected ? alpha('#4DD0E1', .55) : alpha('#94a3b8', .13)}`, bgcolor: selected ? alpha('#4DD0E1', .10) : alpha('#ffffff', .018), color: selected ? '#4DD0E1' : '#94a3b8', fontWeight: 850, fontSize: { xs: '.72rem', sm: '.8rem' } }}>{label}</ButtonBase>;
            })}
          </Box>

          <TextField label="Importe" type="text" fullWidth autoFocus={!movement} inputRef={amountInputRef} value={amount} onChange={(event) => setAmount(normalizeAmountInput(event.target.value))} disabled={mutation.isPending || !hasAccounts} placeholder="0,00" inputProps={{ inputMode: 'decimal', pattern: '[0-9]*[.,]?[0-9]{0,2}' }} sx={{ '& input': { fontSize: '1.55rem', fontWeight: 900 } }} />

          <Box>
            <Typography sx={{ mb: .65, color: '#94a3b8', fontSize: '.69rem', fontWeight: 850, textTransform: 'uppercase', letterSpacing: .7 }}>
              Fecha del movimiento
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={.65} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Stack direction="row" spacing={.6}>
                {([['today', 'Hoy'], ['yesterday', 'Ayer']] as const).map(([value, label]) => (
                  <ButtonBase
                    key={value}
                    disabled={mutation.isPending}
                    onClick={() => setMovementDate(value === 'today' ? todayIso() : yesterdayIso())}
                    sx={{ flex: 1, minWidth: 74, px: 1.5, py: 1, borderRadius: 2,
                      border: `1px solid ${dateMode === value ? alpha('#4DD0E1', .55) : alpha('#94a3b8', .12)}`,
                      bgcolor: dateMode === value ? alpha('#4DD0E1', .09) : alpha('#ffffff', .014),
                      color: dateMode === value ? '#4DD0E1' : '#94a3b8', fontSize: '.75rem', fontWeight: 800 }}
                  >
                    {label}
                  </ButtonBase>
                ))}
              </Stack>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ProaDatePicker
                  label="Elegir fecha"
                  value={movementDate}
                  onChange={setMovementDate}
                  disabled={mutation.isPending}
                  required
                />
              </Box>
            </Stack>
          </Box>

          {movementType === 'transferencia' ? (
            <Stack spacing={1.25}>
              <Box>
                <Typography sx={{ mb: .65, color: '#94a3b8', fontSize: '.69rem', fontWeight: 850, textTransform: 'uppercase', letterSpacing: .7 }}>Desde</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))' }, gap: .6 }}>
                  {accounts.map((account) => compactAccount(account, sourceAccountId === account.id, () => setSourceAccountId(account.id)))}
                </Box>
              </Box>
              <Box>
                <Typography sx={{ mb: .65, color: '#94a3b8', fontSize: '.69rem', fontWeight: 850, textTransform: 'uppercase', letterSpacing: .7 }}>Hacia</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))' }, gap: .6 }}>
                  {accounts.map((account) => compactAccount(account, destinationAccountId === account.id, () => setDestinationAccountId(account.id), sourceAccountId === account.id))}
                </Box>
              </Box>
            </Stack>
          ) : (
            <>
              <Box>
                <Typography sx={{ mb: .65, color: '#94a3b8', fontSize: '.69rem', fontWeight: 850, textTransform: 'uppercase', letterSpacing: .7 }}>Naturaleza del movimiento</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: .6 }}>
                  {([['personal', 'Personal'], ['professional', 'Profesional']] as const).map(([value, label]) => {
                    const selected = financialNature === value;
                    return <ButtonBase key={value} onClick={() => setFinancialNature(value)} sx={{ py: .85, borderRadius: 2.1, border: `1px solid ${selected ? alpha('#4DD0E1', .5) : alpha('#94a3b8', .12)}`, bgcolor: selected ? alpha('#4DD0E1', .08) : alpha('#ffffff', .014), color: selected ? '#4DD0E1' : '#94a3b8', fontWeight: 850, fontSize: '.75rem' }}>{label}</ButtonBase>;
                  })}
                </Box>
              </Box>

              <Box>
                <Typography sx={{ mb: .65, color: '#94a3b8', fontSize: '.69rem', fontWeight: 850, textTransform: 'uppercase', letterSpacing: .7 }}>Cuenta {movementType === 'ingreso' ? 'que recibe el dinero' : 'desde la que se paga'}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))' }, gap: .6 }}>
                  {orderedAccounts.map((account) => compactAccount(account, accountId === account.id, () => selectAccount(account)))}
                </Box>
                {accountNatureMismatch ? (
                  <Alert severity="info" sx={{ mt: .75, py: .1, '& .MuiAlert-message': { fontSize: '.71rem' } }}>
                    Operación {financialNature === 'professional' ? 'profesional' : 'personal'} en cuenta {selectedAccount?.financial_scope === 'professional' ? 'profesional' : 'personal'}.
                    Se clasificará según la naturaleza elegida; el saldo cambiará en la cuenta seleccionada.
                  </Alert>
                ) : null}
              </Box>

              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: .6 }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: '.69rem', fontWeight: 850, textTransform: 'uppercase', letterSpacing: .7 }}>Categoría</Typography>
                  {moreCategoryOptions.length > 0 ? <Button size="small" onClick={() => setShowMoreCategories((value) => !value)} endIcon={showMoreCategories ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />} sx={{ minWidth: 0, px: .5, color: '#94a3b8', fontSize: '.67rem', textTransform: 'none' }}>{showMoreCategories ? 'Menos' : 'Más categorías'}</Button> : null}
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3,minmax(0,1fr))', sm: 'repeat(6,minmax(0,1fr))' }, gap: .5 }}>
                  {quickCategoryOptions.map((option) => {
                    const selected = category === option.value;
                    return <ButtonBase key={option.value} onClick={() => { setCategory(option.value); setCustomCategory(''); }} sx={{ minWidth: 0, borderRadius: 2 }}><Box sx={{ width: '100%', minHeight: 60, px: .4, py: .7, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: .35, borderRadius: 2, border: `1px solid ${selected ? alpha('#4DD0E1', .5) : alpha('#94a3b8', .11)}`, bgcolor: selected ? alpha('#4DD0E1', .08) : alpha('#ffffff', .014) }}><Box sx={{ display: 'flex', color: selected ? '#4DD0E1' : '#64748b', '& svg': { fontSize: 18 } }}>{option.icon}</Box><Typography sx={{ color: selected ? '#f8fafc' : '#94a3b8', fontSize: '.61rem', fontWeight: 800, lineHeight: 1.08, textAlign: 'center' }}>{option.label}</Typography></Box></ButtonBase>;
                  })}
                </Box>

                <Collapse in={showMoreCategories}>
                  <Box sx={{ mt: .6, display: 'grid', gridTemplateColumns: { xs: 'repeat(3,minmax(0,1fr))', sm: 'repeat(4,minmax(0,1fr))' }, gap: .5 }}>
                    {moreCategoryOptions.map((option) => {
                      const selected = category === option.value;
                      return <ButtonBase key={option.value} onClick={() => { setCategory(option.value); if (option.value !== 'Otros') setCustomCategory(''); }} sx={{ borderRadius: 2 }}><Box sx={{ width: '100%', minHeight: 46, px: .45, py: .6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .4, borderRadius: 2, border: `1px solid ${selected ? alpha('#4DD0E1', .5) : alpha('#94a3b8', .10)}`, bgcolor: selected ? alpha('#4DD0E1', .08) : alpha('#ffffff', .012) }}><Box sx={{ display: 'flex', color: selected ? '#4DD0E1' : '#64748b', '& svg': { fontSize: 16 } }}>{option.icon}</Box><Typography sx={{ color: selected ? '#f8fafc' : '#94a3b8', fontSize: '.64rem', fontWeight: 800, lineHeight: 1.08, textAlign: 'center' }}>{option.label}</Typography></Box></ButtonBase>;
                    })}
                  </Box>
                </Collapse>

                {category === 'Otros' ? <TextField label="Nombre de la categoría" fullWidth autoFocus value={customCategory} onChange={(event) => setCustomCategory(event.target.value)} sx={{ mt: .75 }} /> : null}
              </Box>
            </>
          )}

          <Button variant="text" onClick={() => setShowDetails((value) => !value)} endIcon={showDetails ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />} sx={{ alignSelf: 'flex-start', px: 0, color: '#94a3b8', fontSize: '.74rem', fontWeight: 800, textTransform: 'none' }}>{showDetails ? 'Ocultar detalles' : 'Más detalles'}</Button>

          <Collapse in={showDetails}>
            <Stack spacing={1.2}>
              <TextField label="Descripción" placeholder="Opcional" fullWidth value={description} onChange={(event) => setDescription(event.target.value)} disabled={mutation.isPending || !hasAccounts} />
            </Stack>
          </Collapse>

          <Box sx={{ p: 1.1, borderRadius: 2, border: `1px solid ${alpha('#94a3b8', .12)}`, bgcolor: alpha('#ffffff', .02) }}>
            <Typography sx={{ color: '#94a3b8', fontSize: '.68rem', fontWeight: 850, textTransform: 'uppercase', letterSpacing: .5, mb: .35 }}>
              Antes de guardar
            </Typography>
            <Typography sx={{ color: '#e2e8f0', fontSize: '.77rem', lineHeight: 1.5 }}>
              {movementType === 'transferencia'
                ? `${amount || '0,00'} € de ${selectedSourceAccount?.name ?? 'cuenta de origen'} a ${selectedDestinationAccount?.name ?? 'cuenta de destino'}. No constituye un ingreso ni un gasto.`
                : `${movementType === 'gasto' ? 'Sale' : 'Entra'} ${amount || '0,00'} € ${movementType === 'gasto' ? 'de' : 'en'} ${selectedAccount?.name ?? 'la cuenta seleccionada'}. ${movementType === 'gasto' ? 'Gasto' : 'Ingreso'} ${financialNature === 'professional' ? 'profesional' : 'personal'}${effectiveCategory ? ` · ${effectiveCategory}` : ''}.`}
              {' · '}{movementDate || 'Selecciona la fecha'}
            </Typography>
            {fiscalPaymentCategory ? (
              <Typography sx={{ mt: .45, color: '#93c5fd', fontSize: '.69rem', lineHeight: 1.45 }}>
                Pago fiscal/social: se separa de los gastos operativos en la proyección. Comprueba si ya registraste este pago desde Facturas para no duplicarlo.
              </Typography>
            ) : null}
            {movementType === 'ingreso' && financialNature === 'professional' ? (
              <Typography sx={{ mt: .45, color: '#94a3b8', fontSize: '.69rem' }}>
                Registra el importe que realmente has recibido; no vuelvas a introducir un cobro ya contabilizado desde Facturas.
              </Typography>
            ) : null}
            {!movement && movementDate && movementDate > todayIso() ? (
              <Typography sx={{ mt: .45, color: '#fcd34d', fontSize: '.69rem' }}>
                La fecha es futura. Si aún no se ha cobrado o pagado, utiliza Planificación en lugar de registrar un movimiento real.
              </Typography>
            ) : null}
          </Box>
          {!movement ? (
            <Typography sx={{ color: '#64748b', fontSize: '.66rem', lineHeight: 1.4 }}>
              «Guardar + otro» conserva la fecha, la cuenta y el tipo para facilitar el registro de varios movimientos del mismo periodo.
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ position: 'sticky', bottom: 0, zIndex: 2, px: { xs: 2, sm: 3 }, py: 1.35, pb: fullScreen ? 'calc(14px + env(safe-area-inset-bottom))' : 1.8, bgcolor: '#111c2f', borderTop: '1px solid rgba(148,163,184,.10)' }}>
        <Button onClick={onClose} disabled={mutation.isPending} sx={{ mr: 'auto' }}>Cancelar</Button>
        {!movement ? <Button variant="outlined" onClick={() => mutation.mutate('continue')} disabled={mutation.isPending || !isValid} sx={{ textTransform: 'none', fontWeight: 800, whiteSpace: 'nowrap' }}>Guardar + otro</Button> : null}
        <Button variant="contained" onClick={() => mutation.mutate('close')} disabled={mutation.isPending || !isValid} sx={{ fontWeight: 850, textTransform: 'none', whiteSpace: 'nowrap' }}>
          {mutation.isPending ? 'Guardando...' : movement ? 'Guardar' : movementType === 'gasto' ? 'Registrar gasto' : movementType === 'ingreso' ? 'Registrar ingreso' : 'Registrar transferencia'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
